import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Lock, Mail, Loader2, Play } from 'lucide-react';

export default function Login() {
    const { config } = useBranding();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await api.post('/admin/auth/login', { email, password }, {
                skipAuth: true
            } as any);

            if (response.data?.token) {
                login(response.data.token);
                navigate('/');
            } else {
                setError('Login failed: No token received.');
            }
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data || 'Invalid credentials or server error.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen w-full bg-white">
            {/* Left Side - Login Form */}
            <div className="flex w-full flex-col justify-center px-8 lg:w-1/2 lg:px-24">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-6">
                        <img src={config.logoUrl || '/logo.png'} alt="Logo" className="h-9 w-auto" />
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight">{config.portalName}</h1>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome back</h2>
                    <p className="text-slate-500">Please enter your details to sign in.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="p-4 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Email Address</label>
                        <div className="relative group">
                            <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400 group-focus-within:text-brand-primary transition-colors" />
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/10 transition-all text-sm font-medium"
                                placeholder="admin@Xtrem.co.mw"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Password</label>
                        <div className="relative group">
                            <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400 group-focus-within:text-brand-primary transition-colors" />
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/10 transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <label className="flex items-center">
                            <input type="checkbox" className="w-4 h-4 rounded border-slate-350 text-brand-secondary focus:ring-brand-secondary" />
                            <span className="ml-2 text-sm text-slate-600">Remember me</span>
                        </label>
                        <Link to="/forgot-password" className="text-sm font-medium text-brand-primary hover:opacity-80">Forgot password?</Link>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-brand-primary hover:opacity-95 text-white font-bold py-3 rounded-lg transition-all shadow-lg shadow-brand-primary/15 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span>Signing in...</span>
                            </>
                        ) : (
                            <>
                                <span>Sign In</span>
                                <Play className="h-4 w-4 fill-current" />
                            </>
                        )}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <p className="text-xs text-slate-400 font-semibold">
                        © 2026 Xtrem Systems. All rights reserved.
                    </p>
                </div>
            </div>

            {/* Right Side - Hero Gradient */}
            <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-brand-primary via-brand-secondary to-slate-950 items-center justify-center relative overflow-hidden">
                {/* Abstract Shapes for Depth */}
                <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-accent rounded-full mix-blend-overlay filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>

                <div className="relative z-10 p-12 text-white max-w-lg">
                    <div className="mb-8">
                        <div className="h-1.5 w-20 bg-brand-accent rounded-full mb-6"></div>
                        <h2 className="text-4xl font-black mb-6 leading-tight tracking-tight">
                            Enterprise Administration <br />
                            <span className="text-brand-accent">Reimagined.</span>
                        </h2>
                        <p className="text-white/80 text-lg leading-relaxed font-medium">
                            Securely manage policies, claims, and customers with your unified dashboard.
                            Designed for efficiency, built for scale.
                        </p>
                    </div>

                    <div className="flex items-center gap-4 text-sm font-semibold text-white/50">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></div>
                          <span>System Operational</span>
                        </div>
                        <span>•</span>
                        <span>v2.4.0 (Stable)</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
